/**
 * Johnson's Method Parameter Uncertainty for VBL Analysis
 *
 * This module adapts VBL analysis results (using Johnson's method)
 * to work with Monte Carlo VBL prediction by extracting parameter
 * uncertainty information from linear regression standard errors.
 */

import { VblAnalysisResult } from '@/types/vbl';
import { randomNormal } from './parameterUncertainty';

export interface JohnsonParameterUncertainty {
  shape: {
    estimate: number;
    standardError: number;
  };
  scale: {
    estimate: number;
    standardError: number;
  };
  correlation: number;
  sampleSize: number;
  rSquared: number;
  method: 'johnson';
}

/**
 * Estimate correlation between shape and scale parameters from Johnson's regression
 *
 * Johnson's method uses linear regression on log-transformed data:
 * ln(-ln(1-F)) = β*ln(t) - β*ln(η)
 *
 * Both shape (β) and scale (η) come from the same regression line,
 * so they typically have moderate negative correlation.
 */
export function estimateJohnsonCorrelation(vblResult: VblAnalysisResult): number {
  // Empirical relationship: correlation depends on data spread and R²
  const rSquared = vblResult.parameters.rSquared || 0.85;
  const sampleSize = vblResult.summary.sampleSize;

  // Larger samples and better fits tend to have stronger correlation
  // Typical range for Johnson's method: -0.3 to -0.7
  const baseCorrelation = -0.5;
  const rSquaredAdjustment = (rSquared - 0.8) * -0.5; // Stronger fit = stronger (more negative) correlation
  const sampleAdjustment = Math.min(-0.1, -(sampleSize - 10) * 0.01); // More data = stronger (more negative) correlation

  const estimatedCorrelation = baseCorrelation + rSquaredAdjustment + sampleAdjustment;

  // Constrain to reasonable bounds
  return Math.max(-0.8, Math.min(-0.2, estimatedCorrelation));
}

/**
 * Get sample-size-based parameter uncertainty limits
 * Larger samples allow more natural uncertainty, smaller samples stay conservative
 */
function getParameterLimits(sampleSize: number): { maxShapeCV: number; maxLogScaleSE: number } {
  if (sampleSize >= 50) {
    // Large samples: Allow natural uncertainty to propagate
    return { maxShapeCV: 0.20, maxLogScaleSE: 0.15 };
  } else if (sampleSize >= 20) {
    // Medium samples: Moderate constraints
    return { maxShapeCV: 0.10, maxLogScaleSE: 0.08 };
  } else {
    // Small samples: Conservative limits (original behavior)
    return { maxShapeCV: 0.05, maxLogScaleSE: 0.03 };
  }
}

/**
 * Extract parameter uncertainty information from VBL analysis results with validation
 * Adapts Johnson's method results to Monte Carlo compatible format
 */
export function extractJohnsonUncertainty(vblResult: VblAnalysisResult): JohnsonParameterUncertainty | null {
  // Check if we have the required data
  if (!vblResult.parameters || !vblResult.summary) {
    console.warn('VBL result missing required parameters or summary');
    return null;
  }

  // Check for standard errors - should be available from regression
  const standardErrors = vblResult.statistics?.standardErrors;
  if (!standardErrors) {
    console.warn('VBL result missing standard errors - these should be calculated from regression');
    return null;
  }

  const shape = vblResult.parameters.shape;
  const scale = vblResult.parameters.scale;

  // Validate standard errors are reasonable for VBL data
  const shapeCoefficientOfVariation = standardErrors.shape / shape;
  const scaleCoefficientOfVariation = standardErrors.scale; // Already log-scale SE

  // Apply sample-size-based limits for VBL parameter uncertainty
  // Larger samples get relaxed limits, smaller samples stay conservative
  const sampleSize = vblResult.summary.sampleSize;
  const { maxShapeCV, maxLogScaleSE } = getParameterLimits(sampleSize);

  let adjustedShapeSE = standardErrors.shape;
  let adjustedScaleSE = standardErrors.scale;

  // Apply Bayesian shrinkage for extreme uncertainties
  if (shapeCoefficientOfVariation > maxShapeCV) {
    adjustedShapeSE = shape * maxShapeCV;
  }

  if (scaleCoefficientOfVariation > maxLogScaleSE) {
    adjustedScaleSE = maxLogScaleSE;
  }

  // Estimate correlation between parameters
  const correlation = estimateJohnsonCorrelation(vblResult);

  return {
    shape: {
      estimate: shape,
      standardError: adjustedShapeSE
    },
    scale: {
      estimate: scale,
      standardError: adjustedScaleSE // This is log-scale SE
    },
    correlation,
    sampleSize: vblResult.summary.sampleSize,
    rSquared: vblResult.parameters.rSquared || 0,
    method: 'johnson'
  };
}

/**
 * Sample Weibull parameters from Johnson's method uncertainty
 * Uses bivariate normal sampling with estimated correlation
 */
export function sampleJohnsonParameters(
  uncertainty: JohnsonParameterUncertainty,
  numSamples: number = 1000,
  useLogNormalScale: boolean = true,
  covarianceReductionFactor: number = 1.0
): Array<{ shape: number; scale: number }> {

  const samples: Array<{ shape: number; scale: number }> = [];
  const correlation = uncertainty.correlation;

  let validSamples = 0;
  let attempts = 0;
  const maxAttempts = numSamples * 10; // Prevent infinite loop

  while (validSamples < numSamples && attempts < maxAttempts) {
    attempts++;

    // Generate correlated normal variates using bivariate normal
    const z1 = randomNormal(); // Independent standard normal
    const z2 = randomNormal(); // Independent standard normal

    // Apply correlation structure
    const correlatedZ2 = correlation * z1 + Math.sqrt(1 - correlation * correlation) * z2;

    // Sample shape parameter with optional covariance reduction
    const adjustedShapeSE = uncertainty.shape.standardError * Math.sqrt(covarianceReductionFactor);
    const shapeSample = uncertainty.shape.estimate + adjustedShapeSE * z1;

    // Sample scale parameter (with proper log-normal transform)
    let scaleSample: number;
    if (useLogNormalScale) {
      // Sample in log space then transform back (ensures positive scale)
      const logScaleMean = Math.log(uncertainty.scale.estimate);
      const adjustedScaleSE = uncertainty.scale.standardError * Math.sqrt(covarianceReductionFactor);
      const logScaleSample = logScaleMean + adjustedScaleSE * correlatedZ2;
      scaleSample = Math.exp(logScaleSample);
    } else {
      // Direct sampling (may produce negative values)
      const adjustedScaleSE = uncertainty.scale.standardError * Math.sqrt(covarianceReductionFactor);
      scaleSample = uncertainty.scale.estimate + adjustedScaleSE * correlatedZ2;
    }

    // Apply dynamic bounds for VBL data based on actual empirical data
    const minShape = 0.5;    // Minimum reasonable shape for VBL
    const maxShape = 5.0;    // Maximum reasonable shape for VBL
    const minScale = 5.0;    // Minimum degradation time (5 days)

    // Dynamic upper bound for scale based on uncertainty data
    // Use the scale estimate to set reasonable maximum bounds
    const scaleBasedMaximum = uncertainty.scale.estimate * 3.0; // Allow 3x the estimate
    const maxScale = Math.min(2000.0, scaleBasedMaximum); // Cap at 2000 days but use data-driven limit

    // Ensure positive parameters and conservative bounds for VBL data
    if (shapeSample >= minShape && shapeSample <= maxShape &&
        scaleSample >= minScale && scaleSample <= maxScale) {
      samples.push({
        shape: shapeSample,
        scale: scaleSample
      });
      validSamples++;
    }
  }

  if (samples.length === 0) {
    // Return at least the point estimates
    return [{
      shape: uncertainty.shape.estimate,
      scale: uncertainty.scale.estimate
    }];
  }

  return samples;
}

/**
 * Validate Johnson parameter uncertainty for Monte Carlo use
 */
export function validateJohnsonUncertainty(uncertainty: JohnsonParameterUncertainty): {
  isValid: boolean;
  warnings: string[];
} {
  const warnings: string[] = [];

  // Check standard errors are positive
  if (uncertainty.shape.standardError <= 0) {
    warnings.push('Shape standard error should be positive');
  }

  if (uncertainty.scale.standardError <= 0) {
    warnings.push('Scale standard error should be positive');
  }

  // Check correlation is reasonable
  if (Math.abs(uncertainty.correlation) > 0.9) {
    warnings.push('Correlation appears very high - may indicate numerical issues');
  }

  // Check sample size is adequate
  if (uncertainty.sampleSize < 5) {
    warnings.push('Sample size is very small for reliable parameter uncertainty');
  }

  // Check R² is reasonable
  if (uncertainty.rSquared < 0.7) {
    warnings.push('Low R² may indicate poor model fit');
  }

  // Check parameter estimates are reasonable for VBL data
  if (uncertainty.shape.estimate < 0.5 || uncertainty.shape.estimate > 5) {
    warnings.push('Shape parameter outside typical range for VBL data (0.5-5)');
  }

  if (uncertainty.scale.estimate < 1 || uncertainty.scale.estimate > 1000) {
    warnings.push('Scale parameter outside typical range for VBL data (1-1000 days)');
  }

  return {
    isValid: warnings.length === 0,
    warnings
  };
}

/**
 * Calculate approximate confidence intervals for parameters using Johnson's uncertainty
 * This provides an alternative to the Monte Carlo approach for quick estimates
 */
export function calculateJohnsonConfidenceIntervals(
  uncertainty: JohnsonParameterUncertainty,
  confidenceLevel: number = 0.95
): {
  shape: { lower: number; upper: number };
  scale: { lower: number; upper: number };
} {

  const tValue = confidenceLevel === 0.99 ? 2.576 :
                 confidenceLevel === 0.90 ? 1.645 : 1.96;

  return {
    shape: {
      lower: Math.max(0.1, uncertainty.shape.estimate - tValue * uncertainty.shape.standardError),
      upper: uncertainty.shape.estimate + tValue * uncertainty.shape.standardError
    },
    scale: {
      lower: Math.max(1, uncertainty.scale.estimate - tValue * uncertainty.scale.standardError),
      upper: uncertainty.scale.estimate + tValue * uncertainty.scale.standardError
    }
  };
}